import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PostCard } from "@/components/PostCard";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BookOpen, Plus, Search, BookText, Megaphone } from "lucide-react";

const FILTERS = [
  { key: "tum", label: "Tüm Akış" },
  { key: "alinti", label: "Alıntılar" },
  { key: "yorum", label: "İncelemeler" },
  { key: "sozluk", label: "Sözlük Maddeleri" },
  { key: "takip", label: "Takip Ettiklerim" },
  { key: "kitaplar", label: "Kitaplar" },
];

export function ShareMenu({ children, className }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-surface border-line">
        <DropdownMenuItem onClick={() => navigate("/kitaplar")}>Alıntı / Yorum Ekle <span className="text-xs text-muted-foreground ml-1">(kitap seç)</span></DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/sozluk")}>Sözlük Maddesi Ekle</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/onerilerim")}>Kitap Öner</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Dashboard() {
  const { user, glossary } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [feed, setFeed] = useState([]);
  const [filtre, setFiltre] = useState("takip");
  const [q, setQ] = useState("");
  const [sres, setSres] = useState(null);
  const [announcement, setAnnouncement] = useState(null);

  const loadFeed = useCallback(async (f) => {
    const { data } = await api.get("/feed", { params: { filtre: f } });
    setFeed(data);
  }, []);

  useEffect(() => {
    api.get("/daily-quote").then((r) => setQuote(r.data));
    api.get("/announcement").then((r) => setAnnouncement(r.data)).catch(() => {});
    loadFeed("takip");
  }, [loadFeed]);

  useEffect(() => {
    if (q.trim().length < 2) { setSres(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setSres(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);

  const changeFilter = (f) => { setFiltre(f); loadFeed(f); };
  const like = async (id) => {
    const { data } = await api.post(`/posts/${id}/like`);
    setFeed(feed.map((i) => i.id === id ? { ...i, liked_by_me: data.liked, like_count: data.count } : i));
  };
  const save = async (id) => {
    const { data } = await api.post(`/posts/${id}/save`);
    setFeed(feed.map((i) => i.id === id ? { ...i, saved_by_me: data.saved } : i));
  };
  const follow = async (uid, currently) => {
    if (currently) await api.delete(`/users/${uid}/follow`); else await api.post(`/users/${uid}/follow`);
    setFeed(feed.map((i) => i.user_id === uid ? { ...i, takip_ediliyor: !currently } : i));
  };
  const search = (e) => { e.preventDefault(); if (q.trim()) navigate(`/kitaplar?q=${encodeURIComponent(q)}`); };
  const delItem = async (id) => { await api.delete(`/posts/${id}`); setFeed(feed.filter((i) => i.id !== id)); };

  return (
    <div>
      {announcement?.mesaj && (
        <div className="mb-4 rounded-xl border border-gold/40 bg-gold/10 p-4 flex items-start gap-3 fade-up" data-testid="dashboard-announcement">
          <Megaphone className="text-gold shrink-0 mt-0.5" size={20} />
          <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{announcement.mesaj}</p>
        </div>
      )}
      {/* Hero: Günün Sözü + Arama + Paylaş */}
      <section className="rounded-2xl bg-ink text-page p-4 sm:p-5 relative z-20 grain fade-up">
        <div className="relative z-10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Günün Sözü</p>
          <p className="font-serif text-lg sm:text-xl italic leading-snug max-w-3xl">"{quote?.metin || "Sapere Aude!"}"</p>
          <p className="text-xs text-page/70 mt-1">— {quote?.kaynak || "Horatius"}{quote?.erdem ? ` · ${quote.erdem.split("  ")[0]}` : ""}{quote?.topluluktan ? " · topluluktan" : ""}</p>
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            <form onSubmit={search} className="flex gap-2 flex-1 min-w-[240px] relative">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-page/50" size={16} />
                <Input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Kitap, yazar veya üye ara..." className="pl-9 h-11 bg-page/10 border-page/20 text-page placeholder:text-page/40" />
                {sres && (sres.kitaplar.length > 0 || sres.kullanicilar.length > 0 || sres.yazarlar?.length > 0) && (
                  <div className="absolute top-12 left-0 right-0 bg-surface border border-line rounded-lg shadow-xl z-50 overflow-hidden max-h-80 overflow-y-auto" data-testid="search-dropdown">
                    {sres.kullanicilar.map((u) => (
                      <Link key={u.id} to={`/uye/${u.id}`} onClick={() => setSres(null)} className="flex items-center gap-2 px-3 py-2 hover:bg-surface-alt text-ink text-sm">
                        <span className="w-6 h-6 rounded-full bg-terra/10 flex items-center justify-center text-xs text-terra">{u.ad_soyad?.[0]}</span>👤 {u.ad_soyad}
                      </Link>
                    ))}
                    {sres.yazarlar?.map((y) => (
                      <Link key={`y-${y}`} to={`/kitaplar?q=${encodeURIComponent(y)}`} onClick={() => setSres(null)} className="block px-3 py-2 hover:bg-surface-alt text-ink text-sm">
                        ✍️ {y} <span className="text-muted-foreground text-xs">— yazar</span>
                      </Link>
                    ))}
                    {sres.kitaplar.map((b) => (
                      <Link key={b.id} to={`/kitap/${b.id}`} onClick={() => setSres(null)} className="block px-3 py-2 hover:bg-surface-alt text-ink text-sm">
                        📖 {b.baslik} <span className="text-muted-foreground">— {b.yazar}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <button type="submit" data-testid="search-btn" className="h-11 px-5 rounded-lg bg-page/15 text-page font-medium text-sm hover:bg-page/25 shrink-0">Ara</button>
            </form>
            <ShareMenu>
              <button type="button" data-testid="quick-share" className="h-11 px-5 rounded-lg bg-gold text-ink font-medium text-sm flex items-center gap-1.5 hover:bg-gold/90 shrink-0">
                <Plus size={16} /> Paylaş
              </button>
            </ShareMenu>
          </div>
        </div>
      </section>

      {/* Feed */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 mt-6" data-testid="feed-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => changeFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${filtre === f.key ? "bg-terra text-white border-terra" : "border-line text-ink-2 hover:border-terra/40"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="space-y-4" data-testid="feed">
        {feed.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Bu akışta henüz içerik yok.</p>}
        {feed.map((item) => item.item_tur === "sozluk" ? (
          <div key={`gl-${item.id}`} className="border border-line rounded-lg p-5 bg-surface fade-up">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><BookText size={13} className="text-sage" /> Sözlük Maddesi</p>
            <h3 className="font-serif text-xl text-terra">{item.kavram}</h3>
            <p className="text-sm text-ink-2 mt-1 leading-relaxed">{item.tanim}</p>
          </div>
        ) : item.item_tur === "kitap" ? (
          <Link key={`bk-${item.id}`} to={`/kitap/${item.id}`} data-testid={`feed-book-${item.id}`}
            className="flex items-center gap-3 border border-line rounded-lg p-4 bg-surface fade-up hover:border-terra/40 transition-colors group">
            <div className="w-10 h-10 rounded-lg bg-terra/10 border border-terra/15 flex items-center justify-center shrink-0">
              <BookOpen size={18} className="text-terra" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink truncate group-hover:text-terra transition-colors">{item.baslik}</p>
              <p className="text-xs text-muted-foreground truncate">{item.yazar}</p>
            </div>
            <div className="text-right shrink-0">
              {item.ekleyen_adi && <p className="text-xs text-terra">Ekleyen: {item.ekleyen_adi}</p>}
              <p className="text-[11px] text-muted-foreground">{item.tarih ? new Date(item.tarih).toLocaleDateString("tr-TR") : ""}</p>
            </div>
          </Link>
        ) : (
          <div key={item.id} className="fade-up">
            <PostCard post={item} glossary={glossary} showActions liked={item.liked_by_me} saved={item.saved_by_me}
              bookTitle={item.kitap_baslik}
              showFollow isFollowing={item.takip_ediliyor} onFollow={() => follow(item.user_id, item.takip_ediliyor)}
              likeCount={item.like_count} onLike={() => like(item.id)} onSave={() => save(item.id)}
              onDelete={delItem} />
          </div>
        ))}
      </div>
    </div>
  );
}
